import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Paper,
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  LocationOn,
  ShoppingCart,
  People,
  Share,
  Person,
} from '@mui/icons-material';

const CompactDetailRow = ({ label, value, icon, bold = false }) => (
  <Grid container alignItems="center" sx={{ mb: 1 }}>
    <Grid item xs={4} display="flex" alignItems="center">
      {icon}
      <Typography variant="body2" color="text.secondary" ml={1}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={8}>
      <Typography fontWeight={bold ? 'bold' : 'normal'} noWrap>
        {value || '—'}
      </Typography>
    </Grid>
  </Grid>
);

const ReviewRegistration = ({ plan, formData, errors }) => {
  return (
    <Box maxWidth={1200} mx="auto">
      <Typography variant="h6" fontWeight={600} mb={2}>
        Registration Summary
      </Typography>

      <Grid container spacing={2}>
        {/* Top Row - Plan and Basic Info */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1.5}
                display="flex"
                alignItems="center"
              >
                <ShoppingCart fontSize="small" sx={{ mr: 1 }} />
                Plan Details
              </Typography>
              <CompactDetailRow label="Plan" value={plan.name} />
              <CompactDetailRow label="Price" value={`£${plan.price}`} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1.5}
                display="flex"
                alignItems="center"
              >
                <Business fontSize="small" sx={{ mr: 1 }} />
                Company
              </Typography>
              <CompactDetailRow
                label="Name"
                value={formData.companyName}
                bold
              />
              <CompactDetailRow label="Type" value={formData.companyType} />
              <CompactDetailRow
                label="SIC Code"
                value={formData.sicCodes?.join(', ')}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1.5}
                display="flex"
                alignItems="center"
              >
                <Email fontSize="small" sx={{ mr: 1 }} />
                Contact
              </Typography>
              <CompactDetailRow
                label="Name"
                value={formData.name}
                icon={<Person fontSize="small" />}
              />
              <CompactDetailRow
                label="Email"
                value={formData.email}
                icon={<Email fontSize="small" />}
              />
              <CompactDetailRow
                label="Phone"
                value={formData.phone}
                icon={<Phone fontSize="small" />}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Middle Row - Addresses */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1.5}
                display="flex"
                alignItems="center"
              >
                <LocationOn fontSize="small" sx={{ mr: 1 }} />
                Registered Office
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, boxShadow: 1 }}>
                <Typography variant="body2">
                  {formData.registeredAddress.street}
                </Typography>
                <Typography variant="body2">
                  {formData.registeredAddress.city},{' '}
                  {formData.registeredAddress.postalCode}
                </Typography>
                <Typography variant="body2">
                  {formData.registeredAddress.country}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1.5}
                display="flex"
                alignItems="center"
              >
                <People fontSize="small" sx={{ mr: 1 }} />
                Officers
              </Typography>
              {formData.initialOfficers?.map((officer, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{ p: 1.5, mb: 1, boxShadow: 1 }}
                >
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" fontWeight="bold">
                      {officer.name}
                    </Typography>
                    <Chip
                      label={
                        officer.role.charAt(0).toUpperCase() +
                        officer.role.slice(1)
                      }
                      size="small"
                    />
                  </Box>
                  <Box display="flex" mt={0.5}>
                    <Typography variant="caption" mr={2}>
                      {officer.nationality}
                    </Typography>
                    <Typography variant="caption">
                      {officer.dateOfBirth}
                    </Typography>
                  </Box>
                  {officer.address && (
                    <Box mt={1}>
                      <Typography variant="body2" color="text.secondary">
                        Address:
                      </Typography>
                      <Typography variant="body2">
                        {officer.address.street}
                      </Typography>
                      <Typography variant="body2">
                        {officer.address.city}, {officer.address.postalCode}
                      </Typography>
                      <Typography variant="body2">
                        {officer.address.country}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Bottom Row - Shares */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1.5}
                display="flex"
                alignItems="center"
              >
                <Share fontSize="small" sx={{ mr: 1 }} />
                Share Structure
              </Typography>
              <Grid container spacing={2}>
                {formData.shareStructure?.shareClasses?.map(
                  (shareClass, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 1.5, height: '100%', boxShadow: 1 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          {shareClass.type.charAt(0).toUpperCase() +
                            shareClass.type.slice(1)}{' '}
                          Shares
                        </Typography>
                        <CompactDetailRow
                          label="Shares"
                          value={shareClass.totalShares}
                        />
                        <CompactDetailRow
                          label="Value"
                          value={`£${shareClass.valuePerShare} each`}
                        />
                        <CompactDetailRow
                          label="Voting"
                          value={shareClass.votingRights ? 'Yes' : 'No'}
                        />
                      </Paper>
                    </Grid>
                  )
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Total Capital
                    </Typography>
                    <Typography variant="h6">
                      £
                      {formData.shareStructure?.statementOfCapital?.totalAmount}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {errors && (
        <Typography color="error" mt={2}>
          {errors}
        </Typography>
      )}
    </Box>
  );
};

export default ReviewRegistration;
